// Description: this service is used to add filters for STATIC tables
// import { makeAutoObservable } from 'mobx';

import { makeAutoObservable } from 'mobx';

export interface IAppTableFilters {
  isLoading: boolean;
  data: any;
  page: number;
  skip: number;
  take: number;
  filters: any[];
  sorters: SingleSorterType[];
  addFilter: (filter: any) => void;
  setData: (data: any[]) => void;
  getData: () => any[];

  removeFilter: (filter: any) => void;
  getFilteredData: () => any[];

  addSorter: (sorter: SingleSorterType) => void;
  removeSorter: (sorter: SingleSorterType) => void;

  createFilterOptions: () => (data: Record<string, any>[], field: string) => { label: string; value: any }[];
}

export type singleStaticFilterType = {
  id: string;
  type: 'textfields' | 'select' | 'radio' | 'date-range-separate-pickers' | 'numberfields' | 'multioption';
  field: string;
  label: string;
  value: any;
  options?: { label: string; value: any }[];
  filteringFn?: (item: any, value: any) => boolean;
};

export type SingleSorterType = {
  id: string;
  field: string;
  type: 'date' | 'number' | 'text';
  value: 'asc' | 'desc';
  active?: boolean;
  onSort?: (value: 'asc' | 'desc') => void;
};

export class AppTableFilters implements IAppTableFilters {
  isLoading: boolean = false;
  data: any = [];
  page: number = 1;
  skip: number = 0;
  take: number = 10;
  filters: singleStaticFilterType[] = [];
  sorters: SingleSorterType[] = [];

  constructor(take: number = 10) {
    makeAutoObservable(this);
    this.take = take;
  }

  setData = (data: any[]) => {
    this.data = data;
  };

  getData = () => {
    return this.data;
  };

  addFilter = (filter: singleStaticFilterType) => {
    const filterIndex = this.filters.findIndex((f) => f.id === filter.id);

    if (filterIndex === -1) {
      this.filters = [...this.filters, filter];
    } else {
      this.filters = this.filters.map((f) => {
        if (f.id === filter.id) {
          return filter;
        }
        return f;
      });
    }
  };

  removeFilter = (filter: singleStaticFilterType) => {
    const filterIndex = this.filters.findIndex((f) => f.id === filter.id);
    if (filterIndex !== -1) {
      this.filters = this.filters.filter((f) => f.id !== filter.id);
    }
  };

  addSorter = (sorter: SingleSorterType) => {
    const sorterIndex = this.sorters.findIndex((s) => s.id === sorter.id);

    if (sorterIndex === -1) {
      this.sorters = [...this.sorters, { ...sorter, active: true }];
    } else {
      this.sorters = this.sorters.map((s) => {
        if (s.id === sorter.id) {
          return sorter;
        }
        return s;
      });
    }
  };

  isSorterActive = (id) => {
    const sorterIndex = this.sorters.findIndex((s) => s.id === id);
    if (sorterIndex !== -1) {
      return true;
    }
    return false;
  };

  clearSorters = () => {
    this.sorters = [];
  };

  removeSorter = (id) => {
    const sorterIndex = this.sorters.findIndex((s) => s.id === id);
    if (sorterIndex !== -1) {
      this.sorters = this.sorters.filter((s) => s.id !== id);
    }
  };

  getSorter = (id) => {
    const sorterIndex = this.sorters.findIndex((s) => s.id === id);
    if (sorterIndex !== -1) {
      return this.sorters[sorterIndex];
    }
    return null;
  };

  updateSorter = (id, value) => {
    const sorterIndex = this.sorters.findIndex((s) => s.id === id);
    if (sorterIndex !== -1) {
      this.sorters = this.sorters.map((s) => {
        if (s.id === id) {
          return { ...s, value };
        }
        return s;
      });
    }
  };

  getFilteredData = () => {
    if (!this.filters.length && !this.sorters.length) return this.data;

    let filteredData = JSON.parse(JSON.stringify(this.data));

    this.filters.forEach(({ id, type, field, value, filteringFn }) => {
      if (filteringFn) {
        filteredData = filteredData.filter((item) => filteringFn(item, value));
        return;
      }

      if (type === 'textfields') {
        filteredData = filteredData.filter((item) => {
          if (field in item && item[field]?.toLowerCase()?.includes(value?.toLowerCase())) {
            return true;
          }
        });
      }

      if (type === 'numberfields') {
        filteredData = filteredData.filter((item) => {
          if (field in item && item[field] == value) {
            return true;
          }
        });
      }

      if (type === 'select') {
        filteredData = filteredData.filter((item) => {
          if (field in item && item[field].toLowerCase().includes(value.toLowerCase())) {
            return true;
          }
        });
      }

      if (type === 'multioption') {
        filteredData = filteredData.filter((item) => {
          if (field in item && item[field].includes(value)) {
            return true;
          }
        });
      }

      if (type === 'radio') {
        filteredData = filteredData.filter((item) => {
          if (field in item && item[field].toLowerCase() == value.toLowerCase()) {
            return true;
          }
        });
      }
    });

    this.sorters.forEach(({ id, field, value, type }) => {
      if (value === 'asc') {
        filteredData = filteredData.sort((a, b) => {
          if (a[field] < b[field]) {
            return -1;
          }
          if (a[field] > b[field]) {
            return 1;
          }
          return 0;
        });
      }

      if (value === 'desc') {
        filteredData = filteredData.sort((a, b) => {
          if (a[field] > b[field]) {
            return -1;
          }
          if (a[field] < b[field]) {
            return 1;
          }
          return 0;
        });
      }
    });

    return filteredData;
  };

  createFilterOptions = () => {
    let cache = {};

    return function (data: Record<string, any>[], field: string): { label: string; value: string }[] {
      if (!data || !data.length || !field) return [];
      // Check if the result for this field is cached
      if (cache[field]) {
        console.log('Returning from cache', cache[field]);
        return cache[field];
      }

      let options = new Set<string>();

      // Loop through the array and collect unique values
      data.forEach((item) => {
        if (item[field] !== undefined && item[field] !== null) {
          options.add(item[field]);
        }
      });

      // Convert Set back to array
      const optionsArray = Array.from(options).map((o) => ({ label: o, value: o }));

      // Cache the results for future use
      cache[field] = optionsArray;

      console.log('Computed and cached options');
      return optionsArray;
    };
  };
}
